import { Add, CheckCircle, DeleteOutline, MoreVert } from '@mui/icons-material';
import {
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { Fragment, useState } from 'react';
import Alert from '/@/components/bayer/alert';
import {
    useMutateCreateApplicationRegister,
    useMutateDeleteApplicationRegister,
    useMutateUpdateApplicationRegister,
} from '/@/hooks/application-register';
import { useCropProtectionProducts } from '/@/hooks/catalog/use-crop-protection-products';
import { useMutateUpdateCropMonitoringNote } from '/@/hooks/crop-monitoring-notes';
import { useNitrogenSources } from '/@/hooks/nitrogen-smart';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import type { ApplicationRegister } from '/@/types/application-register';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { recordOrigin, trackRegistryChange } from './add/flow-tracking';
import { getUserDisplayName, RegistryFooter, RegistryFooterActions } from './card-footer';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { Cell, COLUMNS, EDIT_COLUMNS } from './data-grid';
import { EditActions } from './edit-actions';
import { normalizeProductName } from './recommendation-helpers';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';
import { TablaCard, TablaCardEditRow, TablaCardRow } from './tabla-card';

const OTHER = 'other';

interface NitrogenSourceOption {
    id: number;
    name: string;
}

interface EditRow {
    /** Existing register id, or undefined for a row added during editing. */
    id?: number;
    date: Dayjs | null;
    /** Product type is fixed (not editable); used to filter the product options. */
    type: number | null;
    productId: number | typeof OTHER | '';
    productName: string;
    dosis: string;
    /** Unit is not editable; shown as a fixed suffix. New rows inherit the group's unit. */
    unity: string;
}

const RegistryApplication = ({
    registers,
    typeName,
    notes,
    recommendedProductNames,
    cropMonitoring,
    expanded,
    onToggle,
}: {
    registers: ApplicationRegister[];
    typeName: string;
    notes: CropMonitoringNote[];
    /** Normalized names of products recommended for the campaign — flags a followed reco. */
    recommendedProductNames?: Set<string>;
    /** The campaign's crop monitoring: gives the tracked events their crop and phenology. */
    cropMonitoring?: CropMonitoring;
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [editing, setEditing] = useState(false);
    const [rows, setRows] = useState<EditRow[]>([]);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const { products } = useCropProtectionProducts();
    const { data: nitrogenSources } = useNitrogenSources() as { data: NitrogenSourceOption[] | undefined };
    const nitrogenSourceOptions = nitrogenSources ?? [];
    const createApplication = useMutateCreateApplicationRegister();
    const updateApplication = useMutateUpdateApplicationRegister();
    const deleteApplication = useMutateDeleteApplicationRegister();
    const updateNote = useMutateUpdateCropMonitoringNote();

    const closeMenu = () => setMenuAnchor(null);

    const first = registers[0];
    if (!first) return null;

    // No crop-protection type at all → this whole group is a "Fertilizantes" application
    // (see add/application.tsx), keyed by nitrogenSource instead of a catalog product.
    const isFertilizante = first.type === null;

    const followedRecommendation = registers.some(
        (register) =>
            register.cropProtectionProductName &&
            recommendedProductNames?.has(normalizeProductName(register.cropProtectionProductName))
    );

    const tracked = {
        fieldId: first.field,
        cropMonitoring,
        registryType: 'application' as const,
        registryOrigin: recordOrigin({
            fromRecommendation: followedRecommendation || registers.some((register) => register.recommendation),
        }),
    };

    const authorName = getUserDisplayName(first.createdBy);

    const toEditRow = (register: ApplicationRegister): EditRow => ({
        id: register.id,
        date: dayjs(register.applicationDate),
        type: register.type,
        // A register created via "Otro" has no catalog product (cropProtectionProduct === null);
        // otherwise pre-select its catalog id (or its nitrogen source id, for Fertilizantes).
        productId: register.nitrogenSource ?? register.cropProtectionProduct ?? OTHER,
        productName:
            register.nitrogenSource == null && register.cropProtectionProduct == null
                ? register.cropProtectionProductName
                : '',
        dosis: String(register.dosis),
        unity: register.unity,
    });

    const startEditing = () => {
        closeMenu();
        setRows(registers.map(toEditRow));
        setEditing(true);
    };

    const cancelEditing = () => {
        setEditing(false);
        setRows([]);
    };

    const updateRow = (index: number, patch: Partial<EditRow>) =>
        setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

    const removeRow = (index: number) => setRows((current) => current.filter((_row, i) => i !== index));

    const addRow = () =>
        setRows((current) => [
            ...current,
            {
                date: dayjs(first.applicationDate),
                type: first.type,
                productId: '',
                productName: '',
                dosis: '',
                unity: first.unity,
            },
        ]);

    const startDeleting = () => {
        closeMenu();
        setConfirmingDelete(true);
    };

    const handleDelete = async () => {
        for (const register of registers) {
            await deleteApplication.trigger({ id: register.id });
        }
        trackRegistryChange('Field Registry Deleted', tracked);
    };

    // Catalog products for a row's (fixed) type, or nitrogen sources for a Fertilizantes group.
    // An empty catalog list means the only choice is free-text — so we skip the selector and
    // treat the row as "Otro" (Fertilizantes rows always have real options, no free-text case).
    const productOptions = (type: number | null): { id: number; name: string }[] =>
        isFertilizante ? nitrogenSourceOptions : products.filter((product) => product.type?.id === type);
    const rowIsFreeText = (row: EditRow) =>
        !isFertilizante && (row.productId === OTHER || productOptions(row.type).length === 0);

    const rowIsValid = (row: EditRow) =>
        Boolean(row.date) &&
        (rowIsFreeText(row) ? row.productName.trim() !== '' : row.productId !== '') &&
        row.dosis.trim() !== '';

    const isMutating = createApplication.isMutating || updateApplication.isMutating || deleteApplication.isMutating;
    const isValid = rows.length > 0 && rows.every(rowIsValid);

    const handleSave = async () => {
        if (!isValid) return;

        // Existing registers no longer present among the edited rows will be deleted.
        const keptIds = new Set(rows.map((row) => row.id).filter((id): id is number => id != null));
        const deletedRegisters = registers.filter((register) => !keptIds.has(register.id));

        // Update kept rows and create new ones first, so there is a surviving register to
        // reattach notes to before we delete anything.
        let createdSurvivorId: number | undefined;
        for (const row of rows) {
            if (!row.date) continue;
            const isOther = rowIsFreeText(row);
            const payload = isFertilizante
                ? {
                      field: first.field,
                      applicationDate: row.date.format('YYYY-MM-DD'),
                      type: null,
                      nitrogenSource: row.productId as number,
                      cropProtectionProduct: null,
                      cropProtectionProductName:
                          nitrogenSourceOptions.find((source) => source.id === row.productId)?.name ?? '',
                      dosis: Number(row.dosis),
                      unity: row.unity,
                  }
                : {
                      field: first.field,
                      applicationDate: row.date.format('YYYY-MM-DD'),
                      type: row.type,
                      cropProtectionProduct: isOther ? null : (row.productId as number),
                      cropProtectionProductName: isOther
                          ? row.productName.trim()
                          : (products.find((p) => p.id === row.productId)?.name ?? ''),
                      dosis: Number(row.dosis),
                      unity: row.unity,
                  };
            if (row.id != null) {
                await updateApplication.trigger({ id: row.id, ...payload });
            } else {
                const created = await createApplication.trigger(payload);
                if (createdSurvivorId === undefined) createdSurvivorId = created?.id;
            }
        }

        // Notes are CASCADE-deleted with their register. Before deleting a row, move any of
        // its notes to a surviving register so the comments are kept on the application.
        const deletedIds = new Set(deletedRegisters.map((register) => register.id));
        const survivorId = rows.find((row) => row.id != null)?.id ?? createdSurvivorId;
        if (survivorId !== undefined) {
            const orphanedNotes = notes.filter(
                (note) => note.applicationRegister != null && deletedIds.has(note.applicationRegister)
            );
            for (const note of orphanedNotes) {
                await updateNote.trigger({ id: note.id, applicationRegister: survivorId });
            }
        }

        for (const register of deletedRegisters) {
            await deleteApplication.trigger({ id: register.id });
        }

        trackRegistryChange('Field Registry Edited', tracked);
        cancelEditing();
    };

    return (
        <>
            <RegistryCard
                header={
                    <Typography variant='h6' color='primary'>
                        {`${_('Aplicación de')} ${typeName}`}
                    </Typography>
                }
                menu={
                    // Mobile has edit/delete icons in the footer instead — the header only has room
                    // for the expand chevron.
                    onToggle ? undefined : (
                        <>
                            <IconButton
                                size='small'
                                aria-label={_('Más opciones')}
                                onClick={(event) => setMenuAnchor(event.currentTarget)}
                            >
                                <MoreVert />
                            </IconButton>
                            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                                <MenuItem onClick={startEditing} disabled={editing}>
                                    {_('Editar')}
                                </MenuItem>
                                <MenuItem onClick={startDeleting} disabled={deleteApplication.isMutating}>
                                    {_('Eliminar')}
                                </MenuItem>
                            </Menu>
                        </>
                    )
                }
                footer={
                    <RegistryFooter
                        authorName={authorName}
                        date={formatDate(new Date(first.created))}
                        actions={
                            onToggle && expanded && !editing ? (
                                <RegistryFooterActions
                                    onEdit={startEditing}
                                    onDelete={startDeleting}
                                    deleteDisabled={deleteApplication.isMutating}
                                />
                            ) : undefined
                        }
                    />
                }
                expanded={expanded}
                onToggle={onToggle}
            >
                {editing ? (
                    <Stack spacing={2}>
                        {onToggle ? (
                            <Stack spacing={1} sx={{ width: '100%' }}>
                                {rows.map((row, index) => {
                                    const typeProducts = productOptions(row.type);
                                    // No catalog products for this type → free text only, no selector.
                                    // Fertilizantes rows always have real options, never free-text.
                                    const freeTextOnly = !isFertilizante && typeProducts.length === 0;
                                    return (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and have no stable id
                                        <TablaCard key={index}>
                                            <TablaCardEditRow label={_('Fecha de aplicación')}>
                                                <MuiDatePicker
                                                    fullWidth
                                                    value={row.date}
                                                    onChange={(value) => updateRow(index, { date: value })}
                                                    slotProps={{
                                                        textField: { placeholder: _('Seleccionar una fecha') },
                                                    }}
                                                />
                                            </TablaCardEditRow>
                                            <TablaCardEditRow label={_('Producto')}>
                                                {freeTextOnly || row.productId === OTHER ? (
                                                    <TextField
                                                        fullWidth
                                                        placeholder={_('Nombre del producto')}
                                                        value={row.productName}
                                                        onChange={(event) =>
                                                            updateRow(index, { productName: event.target.value })
                                                        }
                                                    />
                                                ) : (
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        value={row.productId}
                                                        onChange={(event) => {
                                                            const value = event.target.value;
                                                            updateRow(index, {
                                                                productId: value === OTHER ? OTHER : Number(value),
                                                                productName: '',
                                                            });
                                                        }}
                                                        SelectProps={{ displayEmpty: true }}
                                                    >
                                                        <MenuItem value='' disabled>
                                                            {isFertilizante
                                                                ? _('Elegir fuente de nitrógeno')
                                                                : _('Elegir producto')}
                                                        </MenuItem>
                                                        {typeProducts.map((product) => (
                                                            <MenuItem key={product.id} value={product.id}>
                                                                {product.name}
                                                            </MenuItem>
                                                        ))}
                                                        {!isFertilizante && (
                                                            <MenuItem value={OTHER}>{_('Otro')}</MenuItem>
                                                        )}
                                                    </TextField>
                                                )}
                                            </TablaCardEditRow>
                                            <TablaCardEditRow label={_('Dosis')}>
                                                <TextField
                                                    fullWidth
                                                    type='number'
                                                    placeholder={_('Dosis')}
                                                    value={row.dosis}
                                                    onChange={(event) =>
                                                        updateRow(index, { dosis: event.target.value })
                                                    }
                                                    InputProps={{
                                                        endAdornment: row.unity ? (
                                                            <InputAdornment position='end'>{row.unity}</InputAdornment>
                                                        ) : undefined,
                                                    }}
                                                />
                                            </TablaCardEditRow>
                                            <Button
                                                variant='text'
                                                color='secondary'
                                                startIcon={<DeleteOutline />}
                                                onClick={() => removeRow(index)}
                                                sx={{ alignSelf: 'flex-end' }}
                                            >
                                                {_('Eliminar producto')}
                                            </Button>
                                        </TablaCard>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Box>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: EDIT_COLUMNS,
                                        gap: 1,
                                        borderBottom: 1,
                                        borderColor: 'primary.mui.stroke',
                                    }}
                                >
                                    <Cell header>{_('Fecha de aplicación')}</Cell>
                                    <Cell header>{_('Producto')}</Cell>
                                    <Cell header>{_('Dosis')}</Cell>
                                    <Box />
                                </Box>
                                {rows.map((row, index) => {
                                    const typeProducts = productOptions(row.type);
                                    // No catalog products for this type → free text only, no selector.
                                    // Fertilizantes rows always have real options, never free-text.
                                    const freeTextOnly = !isFertilizante && typeProducts.length === 0;
                                    return (
                                        <Box
                                            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and have no stable id
                                            key={index}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: EDIT_COLUMNS,
                                                alignItems: 'start',
                                                gap: 1,
                                                py: 1.5,
                                                borderBottom: 1,
                                                borderColor: 'primary.mui.stroke',
                                            }}
                                        >
                                            <MuiDatePicker
                                                fullWidth
                                                value={row.date}
                                                onChange={(value) => updateRow(index, { date: value })}
                                                slotProps={{ textField: { placeholder: _('Seleccionar una fecha') } }}
                                            />
                                            {freeTextOnly || row.productId === OTHER ? (
                                                <TextField
                                                    fullWidth
                                                    placeholder={_('Nombre del producto')}
                                                    value={row.productName}
                                                    onChange={(event) =>
                                                        updateRow(index, { productName: event.target.value })
                                                    }
                                                />
                                            ) : (
                                                <TextField
                                                    select
                                                    fullWidth
                                                    value={row.productId}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        updateRow(index, {
                                                            productId: value === OTHER ? OTHER : Number(value),
                                                            productName: '',
                                                        });
                                                    }}
                                                    SelectProps={{ displayEmpty: true }}
                                                >
                                                    <MenuItem value='' disabled>
                                                        {isFertilizante
                                                            ? _('Elegir fuente de nitrógeno')
                                                            : _('Elegir producto')}
                                                    </MenuItem>
                                                    {typeProducts.map((product) => (
                                                        <MenuItem key={product.id} value={product.id}>
                                                            {product.name}
                                                        </MenuItem>
                                                    ))}
                                                    {!isFertilizante && <MenuItem value={OTHER}>{_('Otro')}</MenuItem>}
                                                </TextField>
                                            )}
                                            <TextField
                                                fullWidth
                                                type='number'
                                                placeholder={_('Dosis')}
                                                value={row.dosis}
                                                onChange={(event) => updateRow(index, { dosis: event.target.value })}
                                                InputProps={{
                                                    endAdornment: row.unity ? (
                                                        <InputAdornment position='end'>{row.unity}</InputAdornment>
                                                    ) : undefined,
                                                }}
                                            />
                                            <IconButton
                                                aria-label={_('Eliminar producto')}
                                                onClick={() => removeRow(index)}
                                                sx={{ justifySelf: 'center', alignSelf: 'center' }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        <Button
                            variant='text'
                            color='primary'
                            startIcon={<Add />}
                            sx={{ alignSelf: 'flex-start' }}
                            onClick={addRow}
                        >
                            {_('Agregar otro producto')}
                        </Button>

                        <Divider />

                        <EditActions
                            onCancel={cancelEditing}
                            onSave={handleSave}
                            saveDisabled={!isValid || isMutating}
                            cancelDisabled={isMutating}
                            saveColor={onToggle ? 'primary' : 'secondary'}
                        />
                    </Stack>
                ) : onToggle ? (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        {registers.map((register) => (
                            <TablaCard key={register.id}>
                                <TablaCardRow
                                    label={_('Fecha de aplicación')}
                                    value={formatDate(new Date(register.applicationDate))}
                                />
                                <TablaCardRow label={_('Producto')} value={register.cropProtectionProductName} />
                                <TablaCardRow label={_('Dosis')} value={`${register.dosis} ${register.unity}`.trim()} />
                            </TablaCard>
                        ))}
                    </Stack>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, width: '100%', overflow: 'auto' }}>
                        <Cell header>{_('Fecha de aplicación')}</Cell>
                        <Cell header>{_('Producto')}</Cell>
                        <Cell header>{_('Dosis')}</Cell>
                        {registers.map((register) => (
                            <Fragment key={register.id}>
                                <Cell>{formatDate(new Date(register.applicationDate))}</Cell>
                                <Cell>{register.cropProtectionProductName}</Cell>
                                <Cell>{`${register.dosis} ${register.unity}`.trim()}</Cell>
                            </Fragment>
                        ))}
                    </Box>
                )}

                <RegistryNotes
                    notes={notes}
                    fieldId={first.field}
                    applicationRegisterId={first.id}
                    cropMonitoring={cropMonitoring}
                    registryType={tracked.registryType}
                    registryOrigin={tracked.registryOrigin}
                />

                {followedRecommendation && (
                    <Alert severity='blue' icon={<CheckCircle fontSize='inherit' />}>
                        {_('La aplicación se realizó con el producto que se recomendó.')}
                    </Alert>
                )}
            </RegistryCard>
            <ConfirmDeleteModal
                open={confirmingDelete}
                onClose={() => setConfirmingDelete(false)}
                onConfirm={handleDelete}
                title={_('¿Eliminar la aplicación?')}
                message={_('Esta acción no se puede deshacer.')}
                isDeleting={deleteApplication.isMutating}
            />
        </>
    );
};

export default RegistryApplication;
